import { authenticatedFetch } from './ApiInterceptor';

/**
 * Fetches chatrooms for a specific tournament
 * @param {string} tournamentId - The ID of the tournament
 * @returns {Promise<Array>} List of chatrooms for the tournament
 */
export const fetchTournamentChatrooms = async (tournamentId) => {
  try {
    const response = await authenticatedFetch(`/chatroomsapi/rooms/?tournament_id=${tournamentId}`);

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch chatrooms: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching chatrooms:", error);
    throw error;
  }
};

/**
 * Sends a message to a chatroom
 * @param {string} chatRoomId - The ID of the chatroom
 * @param {string} text - Message text
 * @returns {Promise<Object>} Message details
 */
export const sendChatMessage = async (chatRoomId, text) => {
  try {
    const response = await authenticatedFetch('/chatroomsapi/send/', {
      method: 'POST',
      body: JSON.stringify({
        chat_room: chatRoomId,
        text: text
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to send message: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error sending chat message:", error);
    throw error;
  }
};

/**
 * Fetches messages for a specific chatroom
 * @param {string} chatRoomId - The ID of the chatroom
 * @param {string|null} lastSeen - ISO timestamp of when messages were last fetched
 * @returns {Promise<Array>} List of messages in the chatroom
 */
export const fetchChatMessages = async (chatRoomId, lastSeen = null) => {
  try {
    // Get current time in ISO format if lastSeen not provided
    if (!lastSeen) {
      // Use a timestamp from 10 minutes ago to ensure we don't miss messages
      // due to clock differences between client and server
      const thirtyMinutesAgo = new Date(Date.now() - 33 * 60 * 1000);
      lastSeen = thirtyMinutesAgo.toISOString();
    }

    // Send POST request with both parameters in the request body
    const response = await authenticatedFetch('/chatroomsapi/messages/', {
      method: 'POST',
      body: JSON.stringify({
        chat_room_id: chatRoomId,
        last_seen: lastSeen
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch messages: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();
    
    // Log the message data to debug sender name issues
    console.log("Retrieved chat messages:", data);
    
    return data;
  } catch (error) {
    console.error("Error fetching chat messages:", error);
    throw error;
  }
};
